import { useMemo } from 'react'
import InsightViewOption, { InsightViewOptionStrings } from 'enums/InsightViewOption'
import { useApplicationSettings } from 'hooks/usePageConfig'
import {
  generateIncludedEmojisStrategy,
  generateIncludedProductHierarchyStrategy,
  generateIncludedSentimentDriverStrategy,
  generateIncludedTermsStrategy,
  generateSentimentsWithTermsStrategy,
  generateStarRatingsStrategy,
  generateSurveyScoresStrategy
} from 'utils/insightModal/strategy/widgetSettingComposing'
import { WidgetSettingComposingStrategy } from 'utils/insightModal/strategy/widgetSettingComposing/widgetSettingComposingStrategy'
import {
  pivotProductDecorator,
  pivotTagDecorator,
  pivotThemeDecorator
} from 'utils/insightModal/strategy/widgetSettingComposing/decorator'
import { SideBySideWidgetsType } from '../../types'

const useWidgetSettingComposingStrategy = (
  applicationSettings: any,
  viewOption: InsightViewOptionStrings
): WidgetSettingComposingStrategy =>
  useMemo(() => {
    switch (viewOption) {
      case InsightViewOption.EMOJIS_GROUP:
      case InsightViewOption.EMOJIS_EMOTION:
      case InsightViewOption.EMOJIS_EMOJI:
        return generateIncludedEmojisStrategy()

      case InsightViewOption.PRODUCT_NAME:
        return generateIncludedProductHierarchyStrategy(applicationSettings)

      case InsightViewOption.STAR_RATINGS:
        return generateStarRatingsStrategy()

      case InsightViewOption.SURVEY_SCORES:
        return generateSurveyScoresStrategy()

      case InsightViewOption.ATTRIBUTES:
      case InsightViewOption.EMOTIONS:
      case InsightViewOption.BEHAVIORS:
        return generateIncludedSentimentDriverStrategy(applicationSettings)

      case InsightViewOption.BRANDS:
      case InsightViewOption.HASHTAGS:
      case InsightViewOption.PEOPLE:
      case InsightViewOption.TERMS:
        return generateIncludedTermsStrategy(applicationSettings)

      case InsightViewOption.THINGS:
        return generateSentimentsWithTermsStrategy(applicationSettings)

      default:
        throw new Error(`Unsupported view option: ${viewOption}`)
    }
  }, [viewOption, applicationSettings])

const useDecoratedWidgetSettingComposingStrategy = (widgetSetting: SideBySideWidgetsType) => {
  const applicationSettings = useApplicationSettings()
  const { viewOption, pivotThemeInfos, tagNames, docNames } = widgetSetting

  const widgetSettingComposingStrategy = useWidgetSettingComposingStrategy(
    applicationSettings,
    viewOption!
  )

  if (pivotThemeInfos && pivotThemeInfos.length > 0) {
    return pivotThemeDecorator(widgetSettingComposingStrategy)
  }

  if (tagNames && tagNames.length > 0) {
    return pivotTagDecorator(applicationSettings, widgetSettingComposingStrategy)
  }

  if (docNames && docNames.length > 0) {
    return pivotProductDecorator(applicationSettings, widgetSettingComposingStrategy)
  }

  return widgetSettingComposingStrategy
}

export default useDecoratedWidgetSettingComposingStrategy
